import React from 'react';
import classNames from 'classnames';
import './Text.scss';

type Size = 'xs' | 's' | 'base' | 'm' | 'l' | 'xl' | 'xxl';

const SizeToClassname: Record<Size, string> = {
  xs: 'text--xs',
  s: 'text--s',
  m: 'text--m',
  base: 'text--base',
  l: 'text--l',
  xl: 'text--xl',
  xxl: 'text--xxl',
};

type Color =
  | 'white'
  | 'black'
  | 'grey-300'
  | 'grey-350'
  | 'grey-400'
  | 'grey-500'
  | 'grey-600'
  | 'grey-900'
  | 'accent'
  | 'yellow-600'
  | 'blue-500'
  | 'red-500'
  | 'text-secondary'
  | 'text-muted'
  | 'inherit';

const getColorClassName = (color: Color, invertInLightTheme: boolean = false): string => {
  // Default (dark mode) color classes
  const colorMap: Record<Color, string> = {
    inherit: 'text-inherit',
    white: `text-white ${invertInLightTheme && 'light:text-black'}`,
    black: `text-black ${invertInLightTheme && 'light:text-white'}`,
    'grey-300': `text-grey-300 ${invertInLightTheme && 'light:text-grey-700'}`,
    'grey-350': `text-grey-350 ${invertInLightTheme && 'light:text-grey-650'}`,
    'grey-400': `text-grey-400 ${invertInLightTheme && 'light:text-grey-600'}`,
    'grey-500': `text-grey-500 ${invertInLightTheme && 'light:text-grey-500'}`,
    'grey-600': `text-grey-600 ${invertInLightTheme && 'light:text-grey-400'}`,
    'grey-900': `text-grey-900 ${invertInLightTheme && 'light:text-grey-100'}`,
    accent: 'text-accent',
    'yellow-600': 'text-yellow-600',
    'blue-500': 'text-blue-500',
    'red-500': 'text-red-500',
    'text-secondary': `text-text-secondary`,
    'text-muted': `text-text-muted`,
  };

  return colorMap[color];
};

type Weight = 'regular' | 'medium' | 'semibold' | 'bold';

const WeightToClassname: Record<Weight, string> = {
  regular: 'font-normal',
  medium: 'font-medium',
  semibold: 'font-semibold',
  bold: 'font-bold',
};

export type TextProps = {
  children: React.ReactNode;
  size?: Size;
  color?: Color;
  weight?: Weight;
  center?: boolean;
  className?: string;
  invertInLightTheme?: boolean;
  block?: boolean;
};

export const Text = ({
  size = 'm',
  color = 'inherit',
  weight = 'regular',
  center,
  className,
  invertInLightTheme,
  children,
  block,
}: TextProps) => {
  return (
    <span
      className={classNames(
        'text',

        SizeToClassname[size],
        getColorClassName(color, invertInLightTheme),
        WeightToClassname[weight],
        {
          'text--center': center,
          block: block,
          'inline-block': !block,
        },
        className
      )}
    >
      {children}
    </span>
  );
};
